import { useQuery } from "@tanstack/react-query";
import type {
  SellerProductReviewListResponse,
  SellerProductReviewStatus,
} from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";

type UseSellerReviewsQueryParams = {
  storeSlug: string;
  status?: SellerProductReviewStatus;
  productId?: string;
  limit?: number;
  offset?: number;
};

function buildPath(params: Omit<UseSellerReviewsQueryParams, "storeSlug">) {
  const query = new URLSearchParams();
  if (params.status) {
    query.set("status", params.status);
  }
  if (params.productId) {
    query.set("productId", params.productId);
  }
  if (params.limit !== undefined) {
    query.set("limit", String(params.limit));
  }
  if (params.offset !== undefined) {
    query.set("offset", String(params.offset));
  }
  const queryString = query.toString();
  return queryString.length > 0
    ? `${endpoints.seller.reviews}?${queryString}`
    : endpoints.seller.reviews;
}

export function useSellerReviewsQuery(params: UseSellerReviewsQueryParams) {
  const { storeSlug, ...rest } = params;
  const query = useQuery({
    queryKey: queryKeys.seller.reviews(storeSlug, {
      status: rest.status,
      productId: rest.productId,
      limit: rest.limit,
      offset: rest.offset,
    }),
    queryFn: () =>
      apiJsonClient<SellerProductReviewListResponse>(buildPath(rest)),
    enabled: storeSlug.length > 0,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
